import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { EnTetePage } from '../components/Elements';

/**
 * Mode operatoire : ouvrir une nouvelle annee scolaire par les ecrans.
 *
 * C'est une page de DOCUMENTATION, volontairement statique : elle decrit
 * la procedure validee (creation en preparation, reprise du referentiel,
 * revision des tarifs avant toute inscription, grille, calendrier,
 * bascule a la rentree). L'exemple file est 2026-27, mais la procedure
 * vaut pour chaque rentree.
 */

function Etape({
  numero,
  titre,
  ecran,
  chemin,
  children,
}: {
  numero: number;
  titre: string;
  /** L'ecran ou se joue l'etape, lie pour s'y rendre directement. */
  ecran: string;
  chemin: string;
  children: ReactNode;
}) {
  return (
    <section className="carte p-6">
      <div className="mb-3 flex items-baseline gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-encre-800 text-sm font-semibold text-white">
          {numero}
        </span>
        <h2 className="text-base font-semibold text-encre-900">{titre}</h2>
        <Link to={chemin} className="ml-auto whitespace-nowrap text-sm text-encre-500 underline hover:text-encre-900">
          {ecran}
        </Link>
      </div>
      <div className="space-y-2 text-sm leading-relaxed text-encre-700">{children}</div>
    </section>
  );
}

function Attention({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
      {children}
    </p>
  );
}

export function ModeOperatoireAnneePage() {
  return (
    <>
      <EnTetePage
        titre="Ouvrir une nouvelle année scolaire"
        description="La procédure complète, dans l’ordre : chaque étape se fait aux écrans, sur l’entité affichée dans le bandeau. Exemple filé : la rentrée 2026-27."
        actions={
          <Link to="/annees" className="bouton-secondaire">
            ← Années scolaires
          </Link>
        }
      />

      <div className="mb-4 space-y-2 text-sm text-encre-600">
        <p>
          <strong className="text-encre-800">Compte requis :</strong> Direction ou super admin. La
          nouvelle année vit en <strong>préparation</strong> pendant que l’année courante tourne :
          les deux se saisissent en parallèle, et l’activation n’a lieu qu’à la rentrée.
        </p>
      </div>

      <div className="space-y-4">
        <Etape numero={1} titre="Créer l’année" ecran="Années scolaires" chemin="/annees">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Bouton <strong>« Nouvelle année »</strong>. Libellé au format usuel — ex.{' '}
              <strong>2026-27</strong>. Pour les dates, s’aligner sur l’existant : du{' '}
              <strong>01/09/2026</strong> au <strong>31/08/2027</strong>, comme 2025-26. Les bornes
              sont descriptives — les règlements de pré-inscription de juillet-août se rattachent
              quand même à l’année.
            </li>
            <li>
              <strong>Montant d’adhésion</strong> : 20,00 € proposé — à réviser si l’AG a voté un
              autre montant.
            </li>
            <li>
              Bloc <strong>« Reprendre de l’année précédente »</strong> : laisser les trois cases
              cochées — <strong>Types d’activité et tarifs</strong>, <strong>Activités</strong>,{' '}
              <strong>Types de réduction</strong>. Tarifs, durées, capacités, tolérances, option
              Forfait d’Essai et montants des réductions sont copiés tels quels.
            </li>
            <li>
              L’année naît en statut <strong>préparation</strong> — c’est voulu, ne pas l’activer
              maintenant.
            </li>
          </ul>
        </Etape>

        <Etape numero={2} titre="Réviser les tarifs" ecran="Types et tarifs" chemin="/types-activite">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Passer le <strong>sélecteur d’année</strong> sur la nouvelle année.
            </li>
            <li>
              C’est <strong>le</strong> moment de réviser les tarifs : tant qu’aucune inscription
              n’est saisie sur la nouvelle année, ils sont modifiables.
            </li>
            <li>
              Vérifier capacités et tolérances ; sur l’écran{' '}
              <Link to="/activites" className="underline">
                Activités
              </Link>{' '}
              (même sélecteur), ajuster ou désactiver ce qui ne rouvre pas.
            </li>
          </ul>
          <Attention>
            Dès la <strong>première inscription</strong> saisie sur un type, son tarif se
            verrouille pour l’année. Arrêter la grille tarifaire <strong>avant</strong> de saisir
            la moindre inscription.
          </Attention>
        </Etape>

        <Etape numero={3} titre="Reprendre la grille de cours" ecran="Créneaux" chemin="/creneaux">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Sélecteur d’année sur la nouvelle année, puis bouton{' '}
              <strong>« Reprendre une année »</strong> depuis l’année précédente.
            </li>
            <li>
              Les créneaux sont recopiés et rattachés aux activités de la nouvelle année,
              rapprochées par libellé — une activité renommée ou non reprise laisse ses créneaux de
              côté, à recréer à la main.
            </li>
            <li>
              Ajuster ensuite horaires, salles et encadrants ; contrôle visuel sur{' '}
              <Link to="/planning" className="underline">
                Planning
              </Link>
              , sélecteur sur la nouvelle année.
            </li>
          </ul>
        </Etape>

        <Etape numero={4} titre="Saisir le calendrier des séances" ecran="Calendrier" chemin="/calendrier">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Sélecteur sur la nouvelle année, bouton <strong>« Modifier le calendrier »</strong>,
              puis le crayon cellule par cellule : cocher les ~30 dates travaillées de chaque jour.
            </li>
            <li>
              Au minimum, saisir les premières semaines de septembre-octobre avant les premières
              inscriptions avec Forfait d’Essai.
            </li>
          </ul>
          <Attention>
            Sans calendrier, le prorata rend le <strong>plein tarif</strong> et la date de fin d’un
            Forfait d’Essai n’est pas calculable. Ne pas sauter cette étape.
          </Attention>
        </Etape>

        <Etape numero={5} titre="Saisir les réinscriptions" ecran="Inscriptions" chemin="/inscriptions">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Dès qu’une année en préparation existe, l’écran Inscriptions affiche un{' '}
              <strong>sélecteur d’année</strong> : le passer sur la nouvelle année (badge orange
              « en préparation ») et saisir normalement — activités et créneaux proposés sont les
              siens.
            </li>
            <li>
              Sur{' '}
              <Link to="/paiements" className="underline">
                Paiements
              </Link>
              , même sélecteur : les chèques de pré-inscription se saisissent sur la nouvelle année
              et se ventilent sur ses inscriptions.
            </li>
            <li>
              Pendant ce temps, l’année courante continue de vivre normalement : il suffit de
              laisser chaque écran sur elle.
            </li>
          </ul>
        </Etape>

        <Etape numero={6} titre="À la rentrée : basculer" ecran="Années scolaires" chemin="/annees">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Activer</strong> la nouvelle année. Effet immédiat : elle devient l’année par
              défaut de tous les écrans, et l’ancienne <strong>repasse en préparation</strong> —
              elle reste saisissable pour les derniers règlements et régularisations (RG-26 : une
              seule année active).
            </li>
            <li>
              Une fois l’ancienne année soldée (rapprochement à zéro, dernières remises déposées) :{' '}
              <strong>Clôturer</strong> — réservé à la Direction, définitif, tout passe en lecture
              seule (RG-27).
            </li>
          </ul>
        </Etape>
      </div>

      <section className="carte mt-4 border-red-200 bg-red-50 p-6">
        <h2 className="mb-2 text-base font-semibold text-red-800">À ne pas faire</h2>
        <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-red-800">
          <li>
            Ne saisir <strong>aucune inscription</strong> sur la nouvelle année avant la révision
            des tarifs (étape 2) : le verrou tomberait sur l’ancienne grille.
          </li>
          <li>
            Aucune commande de reprise de données (<code>seed</code>, <code>import</code>) : les
            reprises, c’est fini — tout se fait aux écrans.
          </li>
        </ul>
      </section>
    </>
  );
}
